
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { Cookie } from "lucide-react";
import { getRpgThemeClasses } from "@/lib/utils";

const CookiePolicy = () => {
  const { darkMode, enableRpgMode } = useSettings();
  const navigate = useNavigate();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  return (
    <div className={cn(
      "min-h-screen p-4 font-montserrat transition-colors duration-300",
      enableRpgMode ? (
        rpgTheme.background + " rpg-parchment"
      ) : (
        darkMode 
          ? "bg-gradient-to-br from-[#1a0930] to-[#3F2E46] text-[#FBF3F1]" 
          : "bg-gradient-to-br from-[#FBF3F1] to-[#D896A4]/10"
      )
    )}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className={cn(
              enableRpgMode ? rpgTheme.text : (darkMode ? "text-[#FBF3F1]" : ""),
              enableRpgMode && "hover:bg-amber-600/20"
            )}
          >
            ← Back to App
          </Button>
        </div>

        <Card className={cn(
          "p-8 shadow-lg border backdrop-blur-sm",
          enableRpgMode ? (
            rpgTheme.cardBackground + " " + rpgTheme.text + " rpg-border"
          ) : (
            darkMode 
              ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
              : "bg-white/90 border-[#D896A4]/10"
          )
        )}>
          <div className="flex items-center mb-6">
            <Cookie className={cn("h-6 w-6 mr-3", enableRpgMode ? "text-amber-500" : (darkMode ? "text-[#00FF99]" : "text-[#6127A0]"))} />
            <h1 className={cn("text-3xl font-bold", enableRpgMode && "drop-shadow-sm")}>
              Cookie Policy
            </h1>
          </div>

          <div className="space-y-6 text-sm leading-relaxed">
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                1. What Are Cookies
              </h2>
              <p>
                Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and enabling essential functionality.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                2. How We Use Cookies
              </h2>
              <p className="mb-3">We use cookies for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic app functionality, including authentication and security</li>
                <li><strong>Preference Cookies:</strong> Remember your settings like theme preferences and layout choices</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use the app to improve our service</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                3. Types of Cookies We Use
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Essential Cookies</h3>
                  <p className="text-sm">
                    These cookies are necessary for the app to function properly. They enable core functionality such as security, network management, and accessibility.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Functional Cookies</h3>
                  <p className="text-sm">
                    These cookies allow us to remember choices you make (such as your username, language, or region) and provide enhanced features.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Analytics Cookies</h3>
                  <p className="text-sm">
                    These cookies help us understand how visitors interact with our app by collecting and reporting information anonymously.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                4. Third-Party Cookies
              </h2>
              <p>
                We may use third-party services that set their own cookies. These services include analytics providers and authentication services. We do not control these cookies directly.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                5. Managing Your Cookie Preferences
              </h2>
              <p className="mb-3">You can control cookies through:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Browser Settings:</strong> Most browsers allow you to view, delete, and block cookies</li>
                <li><strong>App Settings:</strong> You can manage some preferences within the app settings</li>
                <li><strong>Opt-out Links:</strong> For analytics cookies, you can opt out through provider-specific tools</li>
              </ul>
              <p className="mt-3 text-sm text-muted-foreground">
                Note: Disabling essential cookies may impact the functionality of the app.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                6. Cookie Duration
              </h2>
              <p className="mb-3">We use both session and persistent cookies:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until manually deleted</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                7. Updates to This Policy
              </h2>
              <p>
                We may update this Cookie Policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the "last updated" date.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                8. Contact Us
              </h2>
              <p>
                If you have any questions about our use of cookies, please contact us at{" "}
                <a 
                  href="mailto:hello@ameliastewart.co" 
                  className={cn(
                    "hover:underline",
                    enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                      darkMode ? "text-[#AE78F0] hover:text-[#c096ff]" : "text-[#6127A0] hover:text-[#4d1f80]"
                    )
                  )}
                >
                  hello@ameliastewart.co
                </a>
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicy;
